import { VectorTile } from '@mapbox/vector-tile'
import Pbf from 'pbf'
import type { Feature, Geometry, LineString, MultiLineString, Point } from 'geojson'
import { logger } from '../../utils/logger.js'
import { cotrexLayerService } from './cotrex-layer-service.js'
import { cotrexSearchService } from './cotrex-search-service.js'

const log = logger.create('cotrex-geometry')

const TILE_ZOOM = 14

interface ExtractedGeometry {
  geometry: Geometry
  properties: Record<string, unknown>
}

function lngLatToTile(lng: number, lat: number, z: number): [number, number] {
  const n = 1 << z
  const x = Math.floor(((lng + 180) / 360) * n)
  const latRad = (lat * Math.PI) / 180
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n)
  return [Math.min(x, n - 1), Math.min(y, n - 1)]
}

function tileRange(
  bbox: [number, number, number, number],
  z: number
): { minX: number; minY: number; maxX: number; maxY: number } {
  const [minLng, minLat, maxLng, maxLat] = bbox
  const [x0, y0] = lngLatToTile(minLng, maxLat, z)
  const [x1, y1] = lngLatToTile(maxLng, minLat, z)
  return {
    minX: Math.min(x0, x1),
    minY: Math.min(y0, y1),
    maxX: Math.max(x0, x1),
    maxY: Math.max(y0, y1),
  }
}

function mergeLineStrings(features: Feature[]): Geometry {
  const lines: LineString['coordinates'][] = []
  for (const f of features) {
    if (f.geometry.type === 'LineString') {
      lines.push(f.geometry.coordinates)
    } else if (f.geometry.type === 'MultiLineString') {
      lines.push(...f.geometry.coordinates)
    }
  }
  if (lines.length === 0) throw new Error('No line geometries found')
  if (lines.length === 1) return { type: 'LineString', coordinates: lines[0] }
  return { type: 'MultiLineString', coordinates: lines } as MultiLineString
}

class CotrexGeometryExtractor {
  async extractTrailGeometry(trailName: string): Promise<ExtractedGeometry | null> {
    const pmtiles = cotrexLayerService.getPMTilesInstance()
    if (!pmtiles) {
      log.warn('PMTiles instance not available')
      return null
    }

    const entry = await cotrexSearchService.findByName(trailName)
    if (!entry) {
      log.warn(`Trail not found in search index: ${trailName}`)
      return null
    }

    const { minX, minY, maxX, maxY } = tileRange(entry.b, TILE_ZOOM)
    log.info(`Extracting ${trailName}: tiles z${TILE_ZOOM} x[${minX}-${maxX}] y[${minY}-${maxY}]`)

    const features: Feature[] = []
    let props: Record<string, unknown> = {}

    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        const resp = await pmtiles.getZxy(TILE_ZOOM, x, y)
        if (!resp) continue

        const tile = new VectorTile(new Pbf(resp.data))
        const layer = (tile.layers as Partial<typeof tile.layers>)['cotrex_trails']
        if (!layer) continue

        for (let i = 0; i < layer.length; i++) {
          const feat = layer.feature(i)
          if (feat.properties.name === trailName) {
            const geojson = feat.toGeoJSON(x, y, TILE_ZOOM)
            features.push(geojson)
            if (Object.keys(props).length === 0) {
              props = { ...feat.properties }
            }
          }
        }
      }
    }

    if (features.length === 0) {
      log.warn(`No tile features found for trail: ${trailName}`)
      return null
    }

    log.info(`Extracted ${features.length} fragments for ${trailName}`)
    return { geometry: mergeLineStrings(features), properties: props }
  }

  async extractTrailheadGeometry(trailheadName: string): Promise<ExtractedGeometry | null> {
    const pmtiles = cotrexLayerService.getPMTilesInstance()
    if (!pmtiles) return null

    const entry = await cotrexSearchService.findByName(trailheadName)
    if (!entry) return null

    const { minX, minY, maxX, maxY } = tileRange(entry.b, TILE_ZOOM)

    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        const resp = await pmtiles.getZxy(TILE_ZOOM, x, y)
        if (!resp) continue

        const tile = new VectorTile(new Pbf(resp.data))
        const layer = (tile.layers as Partial<typeof tile.layers>)['cotrex_trailheads']
        if (!layer) continue

        for (let i = 0; i < layer.length; i++) {
          const feat = layer.feature(i)
          if (feat.properties.name === trailheadName) {
            const geojson = feat.toGeoJSON(x, y, TILE_ZOOM)
            return {
              geometry: geojson.geometry as Point,
              properties: { ...feat.properties },
            }
          }
        }
      }
    }

    return null
  }
}

export const cotrexGeometryExtractor = new CotrexGeometryExtractor()
